import { useEffect, useRef, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  FORMAT_TEXT_COMMAND,
  IS_APPLE,
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
  type ElementNode,
  type TextFormatType,
} from 'lexical';
import { $setBlocksType, $patchStyleText, $getSelectionStyleValueForProperty } from '@lexical/selection';
import { $getNearestNodeOfType } from '@lexical/utils';
import { $isHeadingNode, $createHeadingNode } from '@lexical/rich-text';
import {
  ListNode,
  $isListNode,
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import {
  TextB,
  TextItalic,
  TextUnderline,
  TextHOne,
  TextHTwo,
  TextHThree,
  ListBullets,
  ListNumbers,
  Palette,
} from '../../design-system/icons';
import { Button } from '../../components/ui/button';
import { useDismissOnOutsidePointer } from '../../lib/useDismissOnOutsidePointer';
import { SCRIPT_TEXT_COLORS } from './config';

type HeadingTag = 'h1' | 'h2' | 'h3';
type ListType = 'bullet' | 'number';

interface ActiveState {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  heading: HeadingTag | null;
  list: ListType | null;
  hasColor: boolean;
}

const EMPTY: ActiveState = {
  bold: false,
  italic: false,
  underline: false,
  heading: null,
  list: null,
  hasColor: false,
};

const HEADING_TOOLTIP_SUFFIX = ' — applies to the whole paragraph';

/** ScriptShortcutsPlugin wires up the matching keyboard shortcut for each of
 *  these — kept here purely as a display hint, not the source of truth. */
const modifierKeyLabel = IS_APPLE ? '⌘' : 'Ctrl+';
const shortcutSuffix = (key: string): string => ` (${modifierKeyLabel}${key})`;

const LIST_INSERT_COMMANDS = {
  bullet: INSERT_UNORDERED_LIST_COMMAND,
  number: INSERT_ORDERED_LIST_COMMAND,
} as const;

const Divider = () => <span className="mx-1 h-5 w-px bg-border" aria-hidden />;

const isHeadingTag = (tag: string | null): tag is HeadingTag => tag === 'h1' || tag === 'h2' || tag === 'h3';
const isListType = (type: string | null): type is ListType => type === 'bullet' || type === 'number';

/** Formatting toolbar for the script editor — the tools a script needs: emphasis,
 *  colour, headings, and lists. Every format is a native Lexical format/style,
 *  so it serialises straight into the doc-JSON. */
export function Toolbar() {
  const [editor] = useLexicalComposerContext();
  const [showColors, setShowColors] = useState(false);
  const [active, setActive] = useState<ActiveState>(EMPTY);
  const paletteRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const sel = $getSelection();
        if (!$isRangeSelection(sel)) {
          setActive(EMPTY);
          return;
        }
        const anchorNode = sel.anchor.getNode();
        const topLevel = anchorNode.getKey() === 'root' ? null : anchorNode.getTopLevelElement();
        const listNode = $getNearestNodeOfType(anchorNode, ListNode);
        const listType = listNode && $isListNode(listNode) ? listNode.getListType() : null;
        const tag = topLevel && $isHeadingNode(topLevel) ? topLevel.getTag() : null;
        setActive({
          bold: sel.hasFormat('bold'),
          italic: sel.hasFormat('italic'),
          underline: sel.hasFormat('underline'),
          heading: isHeadingTag(tag) ? tag : null,
          list: isListType(listType) ? listType : null,
          hasColor: $getSelectionStyleValueForProperty(sel, 'color', '') !== '',
        });
      });
    });
  }, [editor]);

  const closePalette = () => setShowColors(false);
  useDismissOnOutsidePointer(showColors, paletteRef, closePalette);

  const format = (f: TextFormatType) => editor.dispatchCommand(FORMAT_TEXT_COMMAND, f);

  /** Sets the block to heading `tag`, or back to a paragraph when it is already
   *  that heading (a second click toggles off). */
  const setHeading = (tag: HeadingTag, isActive: boolean) =>
    editor.update(() => {
      const sel = $getSelection();
      if (!$isRangeSelection(sel)) return;
      const createBlock: () => ElementNode = isActive ? $createParagraphNode : () => $createHeadingNode(tag);
      $setBlocksType(sel, createBlock);
    });

  /** Starts a list of `type`, or removes the list when it is already that type
   *  (a second click toggles off). */
  const toggleList = (type: ListType) => {
    if (active.list === type) {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
      return;
    }
    editor.dispatchCommand(LIST_INSERT_COMMANDS[type], undefined);
  };

  const applyColor = (hex: string | null) =>
    editor.update(() => {
      const sel = $getSelection();
      if ($isRangeSelection(sel)) $patchStyleText(sel, { color: hex });
    });

  const noBlur = (e: React.MouseEvent) => e.preventDefault();
  const cls = (on: boolean) => (on ? 'bg-accent text-accent-foreground' : '');

  const btn = (label: string, on: boolean, onClick: () => void, icon: React.ReactNode, tooltip: string = label) => (
    <Button
      variant="ghost"
      size="icon-sm"
      type="button"
      title={tooltip}
      aria-label={label}
      aria-pressed={on}
      className={cls(on)}
      onMouseDown={noBlur}
      onClick={onClick}
    >
      {icon}
    </Button>
  );

  const headingBtn = (tag: HeadingTag, label: string, icon: React.ReactNode) =>
    btn(label, active.heading === tag, () => setHeading(tag, active.heading === tag), icon, `${label}${HEADING_TOOLTIP_SUFFIX}`);

  return (
    <div className="sticky top-0 z-10 flex flex-wrap items-center gap-1 border-b border-border bg-background px-3 py-2">
      {btn('Bold', active.bold, () => format('bold'), <TextB size={16} />, `Bold${shortcutSuffix('B')}`)}
      {btn('Italic', active.italic, () => format('italic'), <TextItalic size={16} />, `Italic${shortcutSuffix('I')}`)}
      {btn(
        'Underline',
        active.underline,
        () => format('underline'),
        <TextUnderline size={16} />,
        `Underline${shortcutSuffix('U')}`,
      )}
      <Divider />
      {headingBtn('h1', 'Heading 1', <TextHOne size={16} />)}
      {headingBtn('h2', 'Heading 2', <TextHTwo size={16} />)}
      {headingBtn('h3', 'Heading 3', <TextHThree size={16} />)}
      <Divider />
      {btn('Bulleted list', active.list === 'bullet', () => toggleList('bullet'), <ListBullets size={16} />)}
      {btn('Numbered list', active.list === 'number', () => toggleList('number'), <ListNumbers size={16} />)}
      <Divider />
      <div className="relative" ref={paletteRef}>
        {btn('Text colour', active.hasColor, () => setShowColors((v) => !v), <Palette size={16} />)}
        {showColors ? (
          <div className="absolute left-0 top-full z-10 mt-1 flex items-center gap-2 rounded-lg border border-border bg-popover p-2 shadow-md">
            {SCRIPT_TEXT_COLORS.map((c) => (
              <button
                key={c.name}
                type="button"
                title={c.name}
                aria-label={c.name}
                className="size-5 rounded-sm border border-black/10"
                style={{ background: c.hex }}
                onMouseDown={noBlur}
                onClick={() => {
                  applyColor(c.hex);
                  closePalette();
                }}
              />
            ))}
            <button
              type="button"
              title="Clear colour"
              aria-label="Clear colour"
              className="rounded-sm border border-border px-1 text-xs text-muted-foreground"
              onMouseDown={noBlur}
              onClick={() => {
                applyColor(null);
                closePalette();
              }}
            >
              ✕
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
